import axios from "axios";
import { adminApi } from "../../lib/axios";

export type BulkInviteCampaignStatus =
  | "QUEUED"
  | "SENDING"
  | "WAITING"
  | "HALTED"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";

export interface AdminBulkInviteCampaign {
  id: string;
  sessionId: string;
  brandName: string;
  brandEmail: string | null;
  status: BulkInviteCampaignStatus;
  priority: number;
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  bouncedCount: number;
  cancelledCount: number;
  skippedCount: number;
  createdAt: string;
  completedAt: string | null;
  haltedAt: string | null;
  haltReason: string | null;
}

export interface AdminBulkInviteCampaignsResponse {
  data: AdminBulkInviteCampaign[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface BulkInviteSettings {
  bulkImportEnabled: boolean;
  maxRecipientsPerImport: number;
  maxFileSizeMb: number;
}

export interface AdminBulkInviteSettings extends BulkInviteSettings {
  sessionId: string;
}

// Mirrors BullMQ job priority semantics on the backend: lower number = sent sooner.
export const BULK_INVITE_PRIORITY = {
  HIGH: 1,
  NORMAL: 5,
  LOW: 10,
} as const;

export interface ListCampaignsParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

class BulkInviteAdminService {
  async listCampaigns(
    params: ListCampaignsParams = {}
  ): Promise<AdminBulkInviteCampaignsResponse> {
    try {
      const query = new URLSearchParams();
      if (params.page) query.append("page", String(params.page));
      if (params.limit) query.append("limit", String(params.limit));
      if (params.status && params.status !== "ALL")
        query.append("status", params.status);
      if (params.search) query.append("search", params.search);

      const response = await adminApi.get(
        `/admin/bulk-invites/campaigns?${query.toString()}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to load bulk invite jobs"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async cancelCampaign(id: string) {
    try {
      const response = await adminApi.delete(`/admin/bulk-invites/campaigns/${id}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to cancel job");
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async setCampaignPriority(id: string, priority: number) {
    try {
      const response = await adminApi.patch(
        `/admin/bulk-invites/campaigns/${id}/priority`,
        { priority }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Failed to update priority");
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async getSettings(sessionId: string): Promise<AdminBulkInviteSettings> {
    try {
      const response = await adminApi.get(`/admin/bulk-invites/settings/${sessionId}`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to load bulk invite settings"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }

  async updateSettings(
    sessionId: string,
    dto: {
      bulkImportEnabled?: boolean;
      maxRecipientsPerImport?: number | null;
      maxFileSizeMb?: number | null;
    }
  ): Promise<AdminBulkInviteSettings> {
    try {
      const response = await adminApi.put(
        `/admin/bulk-invites/settings/${sessionId}`,
        dto
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update bulk invite settings"
        );
      }
      throw new Error("An unexpected error occurred");
    }
  }
}

export const bulkInviteAdminService = new BulkInviteAdminService();
